/* eslint-disable react-hooks/exhaustive-deps */
import {Alert, Radio} from "antd"
import cx from "classnames"
import {BaseButton, BasePhoneInput, Icon, IconType, SecondaryButton} from "components"
import {FormLabel} from "components/Form"
import {KlassDropdown} from "components/Select"
import StaffSelect from "components/StaffSelect"
import {formatPhoneNumber, getFullName, toastError} from "helpers"
import React from "react"
import {useTranslation} from "react-i18next"
import {DialerRoutingAction, DialerRoutingActionType} from "types/sms-dialer-settings"
import {ContactRole, UserAutocomplete} from "types/user"
import styles from "./PhoneNumberDetails.module.css"

type Props = {
    beingEdited: boolean
    actionIndex: number
    initialAction: DialerRoutingAction
    staffRoles: ContactRole[]
    canEdit: boolean
    onEdit: (index: number) => void
    onCancel: () => void
    onDone: (action: DialerRoutingAction) => void
    onRemove: (index: number) => void
}

const ACTION_OPTIONS: Array<{type: DialerRoutingActionType; labelKey: string; icon: IconType}> = [
    {type: "transfer_to_number", labelKey: "transferToNumber", icon: "PHONE"},
    {type: "call_profile", labelKey: "callProfile", icon: "USER"},
    {type: "call_student_relationship", labelKey: "callStudentRelationship", icon: "USER"}
]

export const DialerRoutingActionNode: React.FC<Props> = ({
    beingEdited,
    actionIndex,
    initialAction,
    staffRoles,
    canEdit,
    onEdit,
    onCancel,
    onDone,
    onRemove
}) => {
    const {t} = useTranslation(["settings", "common"])
    const [action, setAction] = React.useState<DialerRoutingAction>(initialAction)

    React.useEffect(() => {
        setAction(initialAction)
    }, [beingEdited, initialAction])

    const selectedRole =
        staffRoles.find((role) => role.id === action.studentStaffContactRoleId) ||
        (action.studentStaffContactRole
            ? {id: action.studentStaffContactRole.id, name: action.studentStaffContactRole.name}
            : undefined)

    const validate = () => {
        if (!action.type) {
            toastError(t("smsSettings.dialerSettings.requiredActionType"))
            return false
        }
        if (action.type === "transfer_to_number" && !action.phoneNumber?.trim()) {
            toastError(t("smsSettings.dialerSettings.requiredPhoneNumber"))
            return false
        }
        if (action.type === "call_profile" && !action.staffProfileId) {
            toastError(t("smsSettings.dialerSettings.requiredStaffProfile"))
            return false
        }
        if (action.type === "call_student_relationship" && !action.studentStaffContactRoleId) {
            toastError(t("smsSettings.dialerSettings.requiredStudentRelationship"))
            return false
        }
        return true
    }

    const handleDone = () => {
        if (!validate()) return
        onDone({...action, order: actionIndex})
    }

    const handleTypeChange = (type: DialerRoutingActionType) => {
        setAction({
            order: actionIndex,
            type,
            phoneNumber: type === "transfer_to_number" ? action.phoneNumber || "" : undefined,
            staffProfileId: type === "call_profile" ? action.staffProfileId : undefined,
            staffProfile: type === "call_profile" ? action.staffProfile : undefined,
            studentStaffContactRoleId:
                type === "call_student_relationship" ? action.studentStaffContactRoleId : undefined
        })
    }

    const optionMeta = ACTION_OPTIONS.find((o) => o.type === action.type)
    const actionLabel = t(`smsSettings.dialerSettings.${optionMeta?.labelKey}`)

    const getViewSummary = () => {
        if (action.type === "transfer_to_number") {
            return action.phoneNumber ? formatPhoneNumber(action.phoneNumber) : t("smsSettings.dialerSettings.noValue")
        }
        if (action.type === "call_profile") {
            return action.staffProfile
                ? getFullName(action.staffProfile) || action.staffProfile.name
                : t("smsSettings.dialerSettings.noValue")
        }
        return selectedRole?.name || t("smsSettings.dialerSettings.noValue")
    }

    const getViewNotice = () => {
        switch (action.type) {
            case "call_profile":
                return (
                    <Alert
                        type="warning"
                        showIcon
                        message={t("smsSettings.dialerSettings.staffOnlineNotice")}
                        style={{marginTop: 16}}
                    />
                )
            case "call_student_relationship":
                return (
                    <Alert
                        type="warning"
                        showIcon
                        message={t("smsSettings.dialerSettings.studentRelationshipNotice")}
                        style={{marginTop: 16}}
                    />
                )
            default:
                return null
        }
    }

    if (!beingEdited) {
        return (
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionTitle}>
                        {t("smsSettings.dialerSettings.routingActions")} #{actionIndex + 1}
                    </span>
                    {canEdit && (
                        <div className={styles.actionBtns}>
                            <SecondaryButton title={t("common:action.edit")} onClick={() => onEdit(actionIndex)} />
                            <SecondaryButton title={t("common:action.delete")} onClick={() => onRemove(actionIndex)} />
                        </div>
                    )}
                </div>
                <div className={styles.sectionActionViewContainer}>
                    <div className={styles.sectionActionView}>
                        <Icon
                            className={styles.sectionActionViewIcon}
                            icon={optionMeta?.icon || "PHONE"}
                            color="#1e90ff"
                        />
                        <span className={styles.sectionActionViewLabel}>{actionLabel}</span>
                        <div className={styles.sectionActionViewSummary}>{getViewSummary()}</div>
                    </div>
                    {getViewNotice()}
                </div>
            </div>
        )
    }

    return (
        <div className={styles.section}>
            <span className={styles.sectionTitle}>
                {t("smsSettings.dialerSettings.routingActions")} #{actionIndex + 1}
            </span>
            <div className={styles.sectionContent}>
                <div>
                    <FormLabel label={t("smsSettings.dialerSettings.actionType")} isRequired />
                    <Radio.Group
                        className={styles.radioGroup}
                        value={action.type}
                        onChange={(e) => handleTypeChange(e.target.value)}>
                        {ACTION_OPTIONS.map((option) => (
                            <Radio.Button
                                key={option.type}
                                value={option.type}
                                className={cx({active: action.type === option.type})}>
                                {t(`smsSettings.dialerSettings.${option.labelKey}`)}
                            </Radio.Button>
                        ))}
                    </Radio.Group>
                </div>
                {action.type === "transfer_to_number" && (
                    <div>
                        <FormLabel label={t("smsSettings.dialerSettings.phoneNumber")} isRequired />
                        <BasePhoneInput
                            value={action.phoneNumber || ""}
                            onChange={(phoneNumber) => setAction((prev) => ({...prev, phoneNumber}))}
                        />
                    </div>
                )}

                {action.type === "call_profile" && (
                    <div>
                        <FormLabel label={t("smsSettings.dialerSettings.staffProfile")} isRequired />
                        <StaffSelect
                            value={action.staffProfile || null}
                            onChange={(option: UserAutocomplete) =>
                                setAction((prev) => ({
                                    ...prev,
                                    staffProfileId: option?.profileId,
                                    staffProfile: option
                                        ? {
                                              userId: option.userId,
                                              profileId: option.profileId,
                                              firstName: option.firstName,
                                              lastName: option.lastName,
                                              name: getFullName(option),
                                              customProfileId: option.customProfileId
                                          }
                                        : undefined
                                }))
                            }
                            isClearable
                        />
                        <Alert
                            type="warning"
                            showIcon
                            message={t("smsSettings.dialerSettings.staffOnlineNotice")}
                            style={{marginTop: 16}}
                        />
                    </div>
                )}
                {action.type === "call_student_relationship" && (
                    <div>
                        <FormLabel label={t("smsSettings.dialerSettings.studentRelationship")} isRequired />
                        <KlassDropdown
                            options={staffRoles}
                            value={selectedRole || null}
                            valueKey="id"
                            labelKey="name"
                            placeholder={t("smsSettings.dialerSettings.studentRelationship")}
                            onChange={(option: ContactRole) =>
                                setAction((prev) => ({
                                    ...prev,
                                    studentStaffContactRoleId: option?.id
                                }))
                            }
                            isClearable
                        />
                        <Alert
                            type="warning"
                            showIcon
                            message={t("smsSettings.dialerSettings.studentRelationshipNotice")}
                            style={{marginTop: 16}}
                        />
                    </div>
                )}
            </div>
            <div className={styles.sectionFooter}>
                <SecondaryButton title={t("common:action.cancel")} onClick={onCancel} />
                <BaseButton title={t("common:action.done")} onClick={handleDone} />
            </div>
        </div>
    )
}
